import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Rectangle } from 'react-leaflet';
import L from 'leaflet';
import * as d3 from 'd3';
import { geoMollweide } from 'd3-geo-projection';
import defaultCostParameters from './costParameters.json';
import { DEFAULT_HARDWARE_CONFIG } from './data/hardwareCatalog';
import {
  STRATEGIC_LOCATIONS,
  buildStrategicSiteOrder,
} from './data/strategicLocations';
import { useCostDataSync } from './hooks/useCostDataSync';
import {
  applyHardwareCostToBaseParams,
  computeHardwareCost,
} from './lib/computeHardwareCost';
import { applyFactoryScaleToCostResults } from './lib/factoryScaleCurve';
import ConfigError from './components/ConfigError';
import CostProjectionHardwareSummary from './components/CostProjectionHardwareSummary';
import HardwareCostingModal from './components/HardwareCostingModal';
import LoginPage from './components/LoginPage';
import { useAuth } from './context/AuthContext';

// Load initial state from localStorage or use defaults
const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const TelescopeNetworkPlanner = ({ user, signOut }) => {
  const [networkParams, setNetworkParams] = useState(() => {
    const stored = loadFromLocalStorage('networkParams', null);
    
    // Migrate old numModules format to new numSites format
    if (stored && stored.numModules && !stored.numSites) {
      console.log('Migrating networkParams from numModules to numSites/modulesPerSite format');
      const modulesPerSite = stored.modulesPerSite || 3;
      return {
        numSites: {
          global: Math.ceil((stored.numModules.global || 60) / modulesPerSite),
          geo: Math.ceil((stored.numModules.geo || 18) / modulesPerSite),
          current: 6
        },
        modulesPerSite: modulesPerSite,
        exposureTime: stored.exposureTime || 4,
        numExposures: stored.numExposures || 125,
        nightLength: stored.nightLength || 6,
        revisitsPerNight: stored.revisitsPerNight || 3
      };
    }
    
    return stored || {
      numSites: {
        global: 20,
        geo: 6,
        current: 6
      },
      modulesPerSite: 3,
      exposureTime: 4,
      numExposures: 125,
      nightLength: 6,
      revisitsPerNight: 3
    };
  });

  const [hardwareConfig, setHardwareConfig] = useState(() =>
    loadFromLocalStorage('hardwareCostConfig', DEFAULT_HARDWARE_CONFIG),
  );
  const [showHardwareCosting, setShowHardwareCosting] = useState(false);

  const hardwareCostResult = useMemo(
    () => computeHardwareCost(hardwareConfig),
    [hardwareConfig],
  );
  const telescopeSpecs = hardwareCostResult.telescopeSpecs;
  const telescopesPerModule = hardwareCostResult.telescopesPerModule;

  const [coverageType, setCoverageType] = useState(() =>
    loadFromLocalStorage('coverageType', 'global')
  ); // 'global', 'geo', or 'current'
  const [viewMode, setViewMode] = useState('front'); // 'front' or 'back'
  const [mapProjection, setMapProjection] = useState('leaflet'); // 'leaflet' or 'mollweide'
  const [currentNetwork, setCurrentNetwork] = useState([]);
  const [isConstantMonitoring, setIsConstantMonitoring] = useState(() =>
    loadFromLocalStorage('isConstantMonitoring', false)
  );
  
  // Refs for Mollweide projection
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  
  // Add popup state
  const [showObservingPopup, setShowObservingPopup] = useState(false);
  const [showCostPopup, setShowCostPopup] = useState(false);
  const [costEditMode, setCostEditMode] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(() =>
    loadFromLocalStorage('showPriceBreakdown', true)
  );

  // Mollweide zoom handlers
  const handleMollweideZoomIn = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 1.5);
  };

  const handleMollweideZoomOut = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 0.67);
  };

  const handleMollweideZoomReset = () => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  };

  // Cost parameters per tab - will be synced with Supabase
  const [costParams, setCostParams] = useState(() =>
    loadFromLocalStorage('costParams', {
      global: { years: 3.5, taOperated: true, taMaintained: true },
      geo: { years: 3.5, taOperated: true, taMaintained: true },
      current: { years: 3.5, taOperated: true, taMaintained: true }
    })
  );

  // Base cost parameters (editable in cost table) - will be synced with Supabase
  // Defaults loaded from costParameters.json - now a single shared structure
  const [costBaseParams, setCostBaseParams] = useState(() => {
    const stored = loadFromLocalStorage('costBaseParams', null);
    
    // Migrate old format to new format if needed
    if (stored && stored.global && !stored.costItems) {
      // Old format detected, clear it and use defaults
      console.log('Migrating old cost parameters format to new format');
      localStorage.removeItem('costBaseParams');
      return defaultCostParameters;
    }
    
    // Check if cost items have changed (compare IDs)
    if (stored && stored.costItems) {
      const storedIds = stored.costItems.map(item => item.id).sort().join(',');
      const defaultIds = defaultCostParameters.costItems.map(item => item.id).sort().join(',');
      
      if (storedIds !== defaultIds) {
        // Cost items structure has changed, use defaults
        console.log('Cost items structure changed, using new defaults');
        localStorage.removeItem('costBaseParams');
        return defaultCostParameters;
      }
    }
    
    return stored || defaultCostParameters;
  });

  // Current deployed network locations
  const [currentDeployedLocations] = useState([
    { lat: -31.28, lon: 149.082, name: "OZ", fullName: "Australia", status: "operational" },
    { lat: 35.8078, lon: -121.0447, name: "SKY", fullName: "California", status: "operational" },
    { lat: 34.27543, lon: -118.427548, name: "TESTBED", fullName: "Los Angeles", status: "operational" },
    { lat: 31.6657, lon: -110.6018, name: "WINER", fullName: "Arizona", status: "operational" },
    { lat: 38.2153, lon: -6.62725, name: "E-EYE", fullName: "Portugal", status: "operational" }
  ]);

  // Generate stars only once to prevent background updates
  const [stars] = useState(() => {
    return [...Array(50)].map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      animationDelay: Math.random() * 5,
      animationDuration: 3 + Math.random() * 4
    }));
  });

  // Save to localStorage whenever parameters change
  useEffect(() => {
    localStorage.setItem('networkParams', JSON.stringify(networkParams));
  }, [networkParams]);

  // Supabase real-time sync for cost data
  useCostDataSync(costBaseParams, setCostBaseParams, costParams, setCostParams);

  useEffect(() => {
    localStorage.setItem('coverageType', JSON.stringify(coverageType));
  }, [coverageType]);

  // costBaseParams and costParams are now synced via useCostDataSync hook

  useEffect(() => {
    localStorage.setItem('showPriceBreakdown', JSON.stringify(showPriceBreakdown));
  }, [showPriceBreakdown]);

  useEffect(() => {
    localStorage.setItem('isConstantMonitoring', JSON.stringify(isConstantMonitoring));
  }, [isConstantMonitoring]);

  useEffect(() => {
    localStorage.setItem('hardwareCostConfig', JSON.stringify(hardwareConfig));
  }, [hardwareConfig]);

  const handleApplyHardwareCosting = ({
    config,
    hardwareCostPerModule,
    computeCostPerModule,
  }) => {
    setHardwareConfig(config);
    setCostBaseParams((prev) =>
      applyHardwareCostToBaseParams(
        prev,
        hardwareCostPerModule,
        computeCostPerModule,
      ),
    );
  };

  // Generate FOV tiles with GEO-priority distribution
  const generateFOVTiles = () => {
    const fov = calculateFOV();
    const coverage = calculateSkyCoverage();
    const currentModules = networkParams.numSites[coverageType] * networkParams.modulesPerSite;
    const totalTelescopes = currentModules * telescopesPerModule;
    
    // Each telescope makes multiple pointings per night!
    const totalTiles = totalTelescopes * coverage.pointingsPerTelescope;
    
    const tiles = [];
    
    // Use actual FOV size from telescope specs
    const tileWidth = fov.widthDeg;
    const tileHeight = fov.heightDeg;
    
    let tilesPlaced = 0;
    let currentDec = 0; // Start at GEO belt (declination 0)
    let rowIndex = 0;
    
    while (tilesPlaced < totalTiles) {
      // Adjust tile width for declination to maintain constant solid angle
      // At higher declinations, RA circles shrink, so tiles need to be wider in RA to cover same solid angle
      const cosDec = Math.cos(Math.abs(currentDec) * Math.PI / 180);
      const cappedCosDec = Math.max(cosDec, 0.1); // Prevent extreme stretching near poles
      const adjustedTileWidth = tileWidth / cappedCosDec;
      
      // Calculate tiles needed for this declination row
      const tilesThisRow = Math.floor(360 / adjustedTileWidth);
      const lonSpacingThisRow = 360 / tilesThisRow;
      
      // Place tiles in this declination row
      for (let i = 0; i < tilesThisRow && tilesPlaced < totalTiles; i++) {
        const lon = (i * lonSpacingThisRow) % 360;
        const lonCentered = lon > 180 ? lon - 360 : lon; // Convert to -180 to 180
        
        tiles.push({
          lon: lonCentered,
          lat: currentDec,
          width: adjustedTileWidth,  // Use adjusted width for constant solid angle
          height: tileHeight
        });
        
        tilesPlaced++;
      }
      
      // Move to next declination band
      rowIndex++;
      
      // Alternate between +dec and -dec, moving outward from equator
      if (rowIndex % 2 === 1) {
        // Odd rows: positive declination
        currentDec = Math.ceil(rowIndex / 2) * tileHeight;
      } else {
        // Even rows: negative declination
        currentDec = -(rowIndex / 2) * tileHeight;
      }
      
      // Wrap declination: after reaching ±90, restart from equator for next pass
      if (Math.abs(currentDec) > 90) {
        // Reset to equator and restart middle-out pattern
        const overflow = Math.abs(currentDec) - 90;
        rowIndex = 0;
        currentDec = overflow * Math.sign(currentDec); // Start from equator + small offset
      }
    }
    
    return tiles;
  };

  // Calculate field of view using correct formula
  const calculateFOV = () => {
    const focalLength = telescopeSpecs.aperture * 25.4 * telescopeSpecs.focalRatio; // mm
    const fovWidthRad = 2 * Math.atan(telescopeSpecs.sensorWidth / (2 * focalLength));
    const fovHeightRad = 2 * Math.atan(telescopeSpecs.sensorHeight / (2 * focalLength));
    const fovWidthDeg = fovWidthRad * 180 / Math.PI;
    const fovHeightDeg = fovHeightRad * 180 / Math.PI;
    const fovArea = fovWidthDeg * fovHeightDeg; // square degrees
    
    return {
      widthDeg: fovWidthDeg,
      heightDeg: fovHeightDeg,
      areaSqDeg: fovArea
    };
  };

  // Calculate sky coverage
  const calculateSkyCoverage = () => {
    const fov = calculateFOV();
    const currentModules = networkParams.numSites[coverageType] * networkParams.modulesPerSite;
    const totalTelescopes = currentModules * telescopesPerModule;
    
    if (coverageType === 'geo' && isConstantMonitoring) {
      // GEO Monitoring: Each telescope monitors the same 7.4° FOV continuously
      // Only 1 pointing per telescope, no multiple pointings
      const monitoringFOV = 7.4; // degrees, fixed FOV for monitoring
      const totalSkyCoverageSqDeg = totalTelescopes * monitoringFOV;
      const hemisphereArea = 41253 / 2;
      const hemisphereCoverage = totalSkyCoverageSqDeg / hemisphereArea;
      
      return {
        totalSqDeg: totalSkyCoverageSqDeg,
        hemispheres: hemisphereCoverage,
        pointingsPerTelescope: 1, // Always 1 for monitoring
        totalPointings: totalTelescopes, // Each telescope = 1 pointing
        fovPerTelescope: monitoringFOV
      };
    } else {
      // Original calculation for Global and GEO Survey modes
      const exposureTimeMinutes = networkParams.exposureTime / 60;
      const stackTimeMinutes = exposureTimeMinutes * networkParams.numExposures;
      const nightLengthMinutes = networkParams.nightLength * 60;
      
      // Time available for observations per telescope per night
      const observingTimePerTelescope = nightLengthMinutes / networkParams.revisitsPerNight;
      
      // Number of pointings per telescope per night
      const pointingsPerTelescope = Math.floor(observingTimePerTelescope / stackTimeMinutes);
      
      // Total unique pointings per night
      const totalPointingsPerNight = totalTelescopes * pointingsPerTelescope;
      
      // Total sky coverage
      const totalSkyCoverageSqDeg = totalPointingsPerNight * fov.areaSqDeg;
      
      // Coverage in hemispheres (41,253 sq deg per hemisphere)
      const hemisphereArea = 41253 / 2;
      const hemisphereCoverage = totalSkyCoverageSqDeg / hemisphereArea;
      
      return {
        totalSqDeg: totalSkyCoverageSqDeg,
        hemispheres: hemisphereCoverage,
        pointingsPerTelescope,
        totalPointings: totalPointingsPerNight,
        fovPerTelescope: fov.areaSqDeg
      };
    }
  };

  // Calculate network uptime for maintaining full coverage based on redundancy
  // Assumes 75% individual system uptime and independent failures
  // redundancy = how many times over we have the capacity we need (e.g., 2.5x means we need 40% of sites for 100% coverage)
  const calculateCoverageUptime = (redundancy) => {
    if (redundancy < 1) return 0; // No full coverage possible
    const individualUptime = 0.75;
    // With redundancy R, we can lose (R-1)/R of our sites and still maintain coverage
    // For simplicity, calculate as if we need 1/R of our capacity minimum
    // Network stays at full coverage if at least 1/R of systems are up
    // For high redundancy, this approaches very high uptime
    // Using a simplified model: uptime = 1 - (downtime)^redundancy
    const coverageUptime = 1 - Math.pow(1 - individualUptime, redundancy);
    return coverageUptime;
  };

  // Calculate GEO coverage requirements
  const calculateGEOCoverage = () => {
    const bands = [
      { name: "GEO ±3° (50% of GEO)", latRange: 3, coverage: 0.50, color: "purple-400/80" },
      { name: "GEO ±10° (75% of GEO)", latRange: 10, coverage: 0.75, color: "purple-400/60" },
      { name: "GEO ±15° (95% of GEO)", latRange: 15, coverage: 0.95, color: "purple-400/40" },
      { name: "GEO ±30° (100% of GEO)", latRange: 30, coverage: 1.00, color: "purple-400/20" }
    ];
    
    const fov = calculateFOV();
    const coverage = calculateSkyCoverage();
    
    return bands.map(band => {
      // Calculate area of band (simplified as rectangular strip)
      const bandHeight = band.latRange * 2; // degrees latitude
      const bandWidth = 360; // degrees longitude  
      const bandAreaSqDeg = bandHeight * bandWidth;
      const requiredCoverageSqDeg = bandAreaSqDeg * band.coverage;
      
      // Calculate how much of our total coverage could be dedicated to this band
      const achievableCoverage = coverage.totalSqDeg; // Use full coverage, don't limit with Math.min
      const coveragePercentage = (achievableCoverage / requiredCoverageSqDeg) * 100;
      const redundancy = achievableCoverage / requiredCoverageSqDeg;
      
      return {
        ...band,
        totalAreaSqDeg: bandAreaSqDeg,
        requiredCoverageSqDeg: requiredCoverageSqDeg,
        achievableCoverage: achievableCoverage,
        coveragePercentage: coveragePercentage,
        redundancy: redundancy
      };
    });
  };

  // Generic cost calculation engine - data-driven approach
  const calculateCosts = (numModules, modulesPerSite, years, taOperated, taMaintained, costConfig) => {
    const { profitMargin, costItems } = costConfig;
    const numSites = Math.ceil(numModules / modulesPerSite);
    const results = { 
      items: [], 
      oneTimeCost: 0, 
      recurringCost: 0, 
      totalCost: 0,
      totalProfit: 0,
      totalPrice: 0
    };
    
    if (!costItems || !Array.isArray(costItems)) {
      console.error('Invalid costConfig: costItems is missing or not an array', costConfig);
      return results;
    }
    
    costItems.forEach(item => {
      // Check if flag requirement is met
      if (item.requiresFlag === 'taOperated' && !taOperated) {
        results.items.push({ ...item, cost: 0, price: 0, skipped: true });
        return;
      }
      if (item.requiresFlag === 'taMaintained' && !taMaintained) {
        results.items.push({ ...item, cost: 0, price: 0, skipped: true });
        return;
      }
      
      let cost = item.value; // Base cost without profit
      let units = item.basis === 'per_module' ? numModules : numSites; // Fixed: properly differentiate
      
      // Apply special calculations
      if (item.calculation === 'per_5_modules') {
        units = Math.ceil(numModules / 5);
      }
      
      // Apply multiplier (e.g., 4 trips per year)
      if (item.multiplier) {
        cost *= item.multiplier;
      }
      
      // Apply time period for recurring costs
      if (item.type === 'recurring' && years > 0) {
        const periods = item.period === 'per_day' ? years * 365 :
                        item.period === 'per_month' ? years * 12 : 
                        item.period === 'per_year' ? years : 1;
        cost = cost * units * periods;
        results.recurringCost += cost;
      } else if (item.type === 'one-time') {
        cost = cost * units;
        results.oneTimeCost += cost;
      }
      
      const price = cost * profitMargin;
      results.items.push({ ...item, cost, price, skipped: false });
    });
    
    applyFactoryScaleToCostResults(results, {
      numModules,
      profitMargin,
      factoryScale: costConfig.factoryScale,
    });

    return results;
  };

  useEffect(() => {
    // Generate network configuration with real strategic locations
    const generateNetwork = () => {
      if (coverageType === 'current') {
        // Use current deployed locations for current network
        const modules = currentDeployedLocations.map((location, i) => ({
          id: i,
          name: `TKO ${location.name}`,
          telescopes: telescopesPerModule,
          lat: location.lat,
          lon: location.lon,
          location: location.fullName,
          status: location.status
        }));
        
        setCurrentNetwork(modules);
        return;
      }
      
      const strategicLocations = buildStrategicSiteOrder(STRATEGIC_LOCATIONS);
      
      const modules = [];
      const numSites = networkParams.numSites[coverageType];
      const modulesPerSite = networkParams.modulesPerSite;
      let moduleId = 0;
      let locationIndex = 0;
      
      // Distribute modules based on modulesPerSite setting (1-4 per site)
      for (let siteIdx = 0; siteIdx < numSites; siteIdx++) {
        const location = strategicLocations[locationIndex % strategicLocations.length];
        
        // Add the modules for this site
        for (let j = 0; j < modulesPerSite; j++) {
          modules.push({
            id: moduleId,
            name: `TKO ${moduleId + 1}`,
            telescopes: telescopesPerModule,
            lat: location.lat,
            lon: location.lon,
            location: location.name,
            status: 'active'
          });
          moduleId++;
        }
        
        locationIndex++;
      }
      
      setCurrentNetwork(modules);
    };
    
    generateNetwork();
  }, [
    networkParams.numSites,
    networkParams.modulesPerSite,
    coverageType,
    currentDeployedLocations,
    telescopesPerModule,
  ]);

  const handleInputChange = (field, value) => {
    if (field === 'numSites') {
      setNetworkParams(prev => ({
        ...prev,
        numSites: {
          ...prev.numSites,
          [coverageType]: parseFloat(value) || 0
        }
      }));
    } else {
      setNetworkParams(prev => ({
        ...prev,
        [field]: parseFloat(value) || 0
      }));
    }
  };

  const handleCostChange = (field, value) => {
    setCostParams(prev => ({
      ...prev,
      [coverageType]: {
        ...prev[coverageType],
        [field]: field === 'years' ? (parseFloat(value) || 0) : value
      }
    }));
  };

  const handleCostBaseChange = (itemId, value) => {
    setCostBaseParams(prev => {
      if (!prev.costItems) {
        console.error('costItems not found in costBaseParams');
        return prev;
      }
      return {
        ...prev,
        costItems: prev.costItems.map(item => 
          item.id === itemId 
            ? { ...item, value: parseFloat(value) || 0 }
            : item
        )
      };
    });
  };

  const handleProfitMarginChange = (percentageValue) => {
    setCostBaseParams(prev => ({
      ...prev,
      profitMargin: 1 + (parseFloat(percentageValue) / 100) || 1.0
    }));
  };

  // Group modules by their exact coordinates
  const groupModulesByLocation = (modules) => {
    const grouped = {};
    
    modules.forEach(module => {
      const key = `${module.lat},${module.lon}`;
      if (!grouped[key]) {
        grouped[key] = {
          lat: module.lat,
          lon: module.lon,
          location: module.location,
          modules: [],
          totalTelescopes: 0,
          status: module.status // Preserve status information
        };
      }
      grouped[key].modules.push(module);
      grouped[key].totalTelescopes += module.telescopes;
    });
    
    return Object.values(grouped);
  };

  // Create custom icons for different coverage types
  const createCustomIcon = (coverageType, moduleCount = 1, status = null) => {
    const colors = {
      global: '#22d3ee', // cyan
      geo: '#c084fc',    // purple
      monitoring: '#22c55e', // green
      current: '#3b82f6' // blue (default for current)
    };
    
    // Status-based colors for current network
    const statusColors = {
      'operational': '#22c55e', // green
      'down for weather': '#ef4444', // red
      'Operational with warning(s)': '#f59e0b', // yellow/amber
      'Prospective TKO-2 Site': '#ffffff' // white
    };
    
    let color;
    if (coverageType === 'current' && status) {
      color = statusColors[status] || colors.current;
    } else {
      color = colors[coverageType] || '#22d3ee';
    }
    
    const showCount = moduleCount > 1;
    const fontSize = moduleCount > 99 ? '10px' : moduleCount > 9 ? '11px' : '12px';
    
    return L.divIcon({
      className: 'custom-telescope-marker',
      html: `
        <div style="
          width: 24px; 
          height: 24px; 
          border-radius: 50%; 
          background-color: ${color}; 
          border: 2px solid white;
          box-shadow: 0 0 25px ${color}, 0 0 35px ${color}80, 0 0 45px ${color}40;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            position: absolute;
            inset: 3px;
            border-radius: 50%;
            background-color: ${color}cc;
            animation: pulse 2s infinite;
          "></div>
          ${showCount ? `
          <div style="
            position: relative;
            z-index: 10;
            font-size: ${fontSize};
            font-weight: bold;
            color: white;
            font-family: monospace;
            text-shadow: 0 0 3px rgba(0,0,0,0.8);
          ">${moduleCount}</div>
          ` : ''}
        </div>
        <style>
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        </style>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
  };

  // Render Mollweide projection with D3
  useEffect(() => {
    if (mapProjection !== 'mollweide' || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    // Create projection
    const projection = geoMollweide()
      .scale(Math.min(width, height) / 2.5)
      .translate([width / 2, height / 2])
      .precision(0.1);

    const pathGenerator = d3.geoPath().projection(projection);

    // Add defs for gradients
    const defs = svg.append('defs');
    
    // Background gradient
    const bgGradient = defs.append('radialGradient')
      .attr('id', 'mollweide-bg-gradient')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '60%');
    
    bgGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#1a1a2e');
    
    bgGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#000000');

    // Background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'url(#mollweide-bg-gradient)');

    // Create zoom container
    const g = svg.append('g').attr('class', 'zoom-content');

    // Draw globe outline
    const outline = d3.geoGraticule().outline();
    g.append('path')
      .datum(outline)
      .attr('d', pathGenerator)
      .attr('fill', '#0a0a1a')
      .attr('stroke', '#22d3ee')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6);

    // Draw graticule
    const graticule = d3.geoGraticule().step([30, 30]);
    g.append('path')
      .datum(graticule)
      .attr('d', pathGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#22d3ee')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', 0.3);

    // Draw equator with emphasis
    g.append('path')
      .datum({
        type: 'LineString',
        coordinates: [[-180, 0], [180, 0]]
      })
      .attr('d', pathGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#22d3ee')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.7);

    // GEO belt indicator (subtle band at equator)
    if (coverageType === 'geo') {
      const geoColor = isConstantMonitoring ? '#22c55e' : '#c084fc';
      
      // Draw GEO belt bands
      [-15, -10, -3, 3, 10, 15].forEach((lat, index) => {
        const opacity = index === 2 || index === 3 ? 0.15 : 0.08;
        g.append('path')
          .datum({
            type: 'LineString',
            coordinates: [[-180, lat], [180, lat]]
          })
          .attr('d', pathGenerator)
          .attr('fill', 'none')
          .attr('stroke', geoColor)
          .attr('stroke-width', 1)
          .attr('stroke-opacity', opacity);
      });
    }

    // Add coordinate labels
    const labelGroup = g.append('g').attr('class', 'coordinate-labels');
    
    // Longitude labels (along equator)
    [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150].forEach(lon => {
      const coords = projection([lon, 0]);
      if (coords) {
        const label = labelGroup.append('g')
          .attr('transform', `translate(${coords[0]}, ${coords[1]})`);
        
        // Background for readability
        label.append('rect')
          .attr('x', -16)
          .attr('y', 8)
          .attr('width', 32)
          .attr('height', 14)
          .attr('fill', '#0a0a1a')
          .attr('fill-opacity', 0.8)
          .attr('rx', 2);
        
        label.append('text')
          .attr('x', 0)
          .attr('y', 18)
          .attr('text-anchor', 'middle')
          .attr('fill', '#22d3ee')
          .attr('font-size', 9)
          .attr('font-weight', 500)
          .text(`${lon}°`);
      }
    });
    
    // Latitude labels (along prime meridian)
    [-60, -30, 30, 60].forEach(lat => {
      const coords = projection([0, lat]);
      if (coords) {
        const label = labelGroup.append('g')
          .attr('transform', `translate(${coords[0]}, ${coords[1]})`);
        
        // Background for readability
        label.append('rect')
          .attr('x', -16)
          .attr('y', -10)
          .attr('width', 32)
          .attr('height', 14)
          .attr('fill', '#0a0a1a')
          .attr('fill-opacity', 0.8)
          .attr('rx', 2);
        
        label.append('text')
          .attr('x', 0)
          .attr('y', 0)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('fill', '#22d3ee')
          .attr('font-size', 9)
          .attr('font-weight', 500)
          .text(`${lat > 0 ? '+' : ''}${lat}°`);
      }
    });

    // Generate and draw FOV tiles
    const tiles = generateFOVTiles();
    const tileColor = coverageType === 'global' ? '#22d3ee' :
                      coverageType === 'geo' ? (isConstantMonitoring ? '#22c55e' : '#c084fc') :
                      '#3b82f6';

    // Use fixed opacity - tiles will naturally overlap after full coverage
    const tileOpacity = 0.3;

    tiles.forEach((tile) => {
      const { lon, lat, width, height } = tile;
      
      // Create tile corners
      const halfWidth = width / 2;
      const halfHeight = height / 2;
      
      const corners = [
        [lon - halfWidth, lat + halfHeight], // top-left
        [lon + halfWidth, lat + halfHeight], // top-right
        [lon + halfWidth, lat - halfHeight], // bottom-right
        [lon - halfWidth, lat - halfHeight], // bottom-left
        [lon - halfWidth, lat + halfHeight]  // close the path
      ];

      // Draw tile
      g.append('path')
        .datum({
          type: 'Polygon',
          coordinates: [corners]
        })
        .attr('d', pathGenerator)
        .attr('fill', tileColor)
        .attr('fill-opacity', tileOpacity)
        .attr('stroke', tileColor)
        .attr('stroke-width', 0.5)
        .attr('stroke-opacity', 0.8);
    });

    // Setup zoom behavior
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.8, 8])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoomBehavior);
    zoomBehaviorRef.current = zoomBehavior;

  }, [
    mapProjection,
    coverageType,
    networkParams.numSites,
    networkParams.modulesPerSite,
    isConstantMonitoring,
    telescopeSpecs.aperture,
    telescopeSpecs.focalRatio,
    telescopeSpecs.sensorWidth,
    telescopeSpecs.sensorHeight,
  ]);

  const fovData = calculateFOV();
  const coverage = calculateSkyCoverage();
  const geoCoverage = calculateGEOCoverage();
  const totalModules = networkParams.numSites[coverageType] * networkParams.modulesPerSite;
  const costData = calculateCosts(
    totalModules,
    networkParams.modulesPerSite,
    costParams[coverageType]?.years ?? 3.5,
    costParams[coverageType]?.taOperated ?? true,
    costParams[coverageType]?.taMaintained ?? true,
    costBaseParams
  );

  // Helper function to generate tooltip explaining cost calculation
  const getCostTooltip = (item) => {
    const numSites = networkParams.numSites[coverageType];
    const numModules = totalModules;
    const years = costParams[coverageType]?.years ?? 3.5;
    
    let tooltip = `${item.name}\n\n`;
    
    // Base calculation
    if (item.basis === 'per_module') {
      tooltip += `Base: $${item.value.toLocaleString()} per module\n`;
      tooltip += `Modules: ${numModules}\n`;
    } else if (item.basis === 'per_site') {
      tooltip += `Base: $${item.value.toLocaleString()} per site\n`;
      tooltip += `Sites: ${numSites}\n`;
    }
    
    if (costData.scaleMultiplier > 1) {
      tooltip += `\nFactory scale (Wright's law):\n`;
      tooltip += `- ${costData.scaleMultiplier.toFixed(2)}× on totals (< ${costData.factoryModules ?? 30} modules)\n`;
      if (costData.scaleMultiplierCapped) {
        tooltip += `- Capped at ${costData.scaleMultiplier.toFixed(2)}× (uncapped ${costData.rawScaleMultiplier.toFixed(2)}×)\n`;
      } else {
        tooltip += `- 15% cost reduction per doubling of modules to factory scale\n`;
      }
    }

    // Multipliers
    if (item.multiplier) {
      tooltip += `Multiplier: ${item.multiplier}x ${item.multiplierLabel || ''}\n`;
    }
    
    // Time periods
    if (item.type === 'recurring' && item.period) {
      const periodName = item.period.replace('per_', '');
      tooltip += `Period: ${periodName}\n`;
      tooltip += `Contract: ${years} years\n`;
      
      const periods = item.period === 'per_day' ? years * 365 :
                      item.period === 'per_month' ? years * 12 : 
                      item.period === 'per_year' ? years : 1;
      tooltip += `Total periods: ${periods.toFixed(0)}\n`;
    }
    
    // Flags
    if (item.requiresFlag === 'taOperated') {
      tooltip += `\nRequires: TransAstra Operated\n`;
    }
    if (item.requiresFlag === 'taMaintained') {
      tooltip += `\nRequires: TransAstra Maintained\n`;
    }
    
    return tooltip;
  };

  return (
    <div className="min-h-screen bg-gradient-dark text-sky-300/90 p-8 overflow-hidden relative">
      <style>{`
        @keyframes scan {
          0% { top: 10%; opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { top: 90%; opacity: 0; }
        }
        
        body {
          min-width: 1400px;
          min-height: 900px;
        }
        
        html {
          min-width: 1400px;
          min-height: 900px;
        }
      `}</style>
      
      <div className="fixed inset-0 overflow-hidden">
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute animate-pulse"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              animationDelay: `${star.animationDelay}s`,
              animationDuration: `${star.animationDuration}s`
            }}
          >
            <div className="w-1 h-1 bg-white rounded-full opacity-20"></div>
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Logo in top left corner */}
        <div className="absolute top-0 left-0 z-50">
          <img 
            src="/logo.png" 
            alt="Logo" 
            className="h-12 w-auto opacity-100 hover:opacity-80 transition-opacity"
            onError={(e) => {
              e.target.style.display = 'none';
              console.log('Logo not found at /logo.png');
            }}
          />
        </div>

        {/* User menu - top right */}
        {user && signOut ? (
          <div className="absolute top-0 right-0 z-50 flex items-center gap-3">
            <span className="text-sm font-orbitron text-sky-300/80 tracking-wide">
              {user.displayName ?? user.email}
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="px-4 py-2 text-xs font-orbitron tracking-wider glass-button rounded-lg text-sky-200 hover:glass-button-active transition-all"
            >
              Sign out
            </button>
          </div>
        ) : null}

        {/* Coverage Type Tabs - Four Options */}
        <div className="mb-6 text-center">
          <div className="inline-flex glass-card rounded-2xl p-1.5 gap-1">
            <button
              onClick={() => setCoverageType('global')}
              title="Night sky survey coverage from the telescope network"
              className={`px-6 py-2.5 text-sm font-orbitron tracking-wider rounded-xl transition-all ${
                coverageType === 'global' 
                  ? 'glass-button-active text-sky-200 shadow-glass-glow-cyan' 
                  : 'text-sky-300/60 hover:text-sky-200 glass-button'
              }`}
            >
              GLOBAL SURVEY
            </button>
            <button
              onClick={() => setCoverageType('geo')}
              title="Analyze coverage above and below equator"
              className={`px-6 py-2.5 text-sm font-orbitron tracking-wider rounded-xl transition-all ${
                coverageType === 'geo' 
                  ? 'glass-button-active text-violet-200 shadow-glass-glow-violet' 
                  : 'text-sky-300/60 hover:text-sky-200 glass-button'
              }`}
            >
              GEO ANALYSIS
            </button>
            <button
              onClick={() => setCoverageType('current')}
              title="View the currently deployed telescope network configuration"
              className={`px-6 py-2.5 text-sm font-orbitron tracking-wider rounded-xl transition-all ${
                coverageType === 'current' 
                  ? 'glass-button-active text-blue-200 shadow-glass-glow-cyan' 
                  : 'text-sky-300/60 hover:text-sky-200 glass-button'
              }`}
            >
              CURRENT NETWORK
            </button>
          </div>
        </div>

        {/* TKO Modules Slider and Observing Parameters Button */}
        <div className="grid grid-cols-2 gap-6 mb-6 lg:mb-8 max-w-5xl mx-auto">
          {/* Left Column: Network Sites */}
          <div className="glass-card rounded-2xl px-8 py-6">
            <div className="text-lg font-orbitron tracking-wider mb-3 text-sky-200">
              {coverageType === 'current' ? 'CURRENT NETWORK' : 'NETWORK SITES'}
            </div>
            {coverageType === 'current' ? (
              <div className="text-center">
                <div className="text-lg font-orbitron text-sky-100 mb-2">5 Locations</div>
                <div className="text-sm text-sky-200/70">28 total telescopes</div>
                <div className="text-xs text-sky-300/50 mt-2">Fixed deployment sites</div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {/* Number of Sites Slider */}
                  <div>
                    <div className="text-xs opacity-60 mb-2 text-center">NUMBER OF SITES</div>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="1"
                        max={(coverageType === 'geo' && isConstantMonitoring) ? "250" : "34"}
                        step="1"
                        value={networkParams.numSites[coverageType]}
                        onChange={(e) => handleInputChange('numSites', e.target.value)}
                        className="glass-slider flex-1"
                      />
                      <span className="text-lg font-orbitron text-sky-100 min-w-[2rem]">{networkParams.numSites[coverageType]}</span>
                    </div>
                  </div>
                  
                  {/* Modules per Site Slider */}
                  <div>
                    <div className="text-xs opacity-60 mb-2 text-center">MODULES PER SITE</div>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="1"
                        value={networkParams.modulesPerSite}
                        onChange={(e) => handleInputChange('modulesPerSite', e.target.value)}
                        className="glass-slider flex-1"
                      />
                      <span className="text-lg font-orbitron text-sky-100 min-w-[2rem]">{networkParams.modulesPerSite}</span>
                    </div>
                  </div>
                  
                  {/* Summary */}
                  <div className="text-sm text-sky-200/70 text-center pt-2 border-t border-glass-border">
                    {networkParams.numSites[coverageType] * networkParams.modulesPerSite} modules • {networkParams.numSites[coverageType] * networkParams.modulesPerSite * telescopesPerModule} total telescopes
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Right Column: Stacked Cards */}
          <div className="flex flex-col gap-3">
            {/* Observing Parameters Card */}
            <div className="glass-card px-6 py-4 rounded-2xl flex-1 relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base font-orbitron text-sky-200 tracking-wider">OBSERVING PARAMETERS</div>
                  <div className="text-xs text-sky-300/60 mt-1">{networkParams.exposureTime}s • {networkParams.numExposures} exp • {networkParams.nightLength}h</div>
                </div>
                <button 
                  onClick={() => setShowObservingPopup(true)}
                  className="glass-button p-3 rounded-xl hover:scale-110 transition-all duration-300 group"
                  aria-label="Configure observing parameters"
                >
                  <svg 
                    className="w-5 h-5 text-sky-300 group-hover:text-sky-100 group-hover:rotate-90 transition-all duration-300" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
                    />
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Cost Projection Card */}
            {coverageType !== 'current' ? (
              <div className="glass-card px-6 py-4 rounded-2xl flex-1 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-orbitron text-amber-200/90 tracking-wider">COST PROJECTION</div>
                    <div className="text-xs text-amber-300/60 mt-1 cost-value">
                      ${(costData.totalPrice / 1000000).toFixed(1)}M • {costParams[coverageType].years}y
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowCostPopup(true)}
                    className="glass-button p-3 rounded-xl hover:scale-110 transition-all duration-300 group"
                    aria-label="Configure cost projection"
                  >
                    <svg 
                      className="w-5 h-5 text-amber-300 group-hover:text-amber-100 group-hover:rotate-90 transition-all duration-300" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
                      />
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1"></div>
            )}
          </div>
        </div>

        {/* Map Section - With conditional GEO Info Panel */}
        <div className="mb-6 lg:mb-8 max-w-5xl mx-auto">
          <div className="relative">
            <div className="relative glass-card rounded-2xl p-4 lg:p-8">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex-1"></div>
                <div className="text-lg font-orbitron text-sky-200 tracking-wider">
                  {coverageType === 'global' ? 'GLOBAL TKO DEPLOYMENT' : 
                   coverageType === 'geo' ? 'GEO COVERAGE' :
                   'CURRENT DEPLOYED NETWORK'}
                </div>
                <div className="flex-1 flex justify-end">
                  {/* Constant Monitoring Toggle - Only for GEO - Top Right of Card */}
                  {coverageType === 'geo' && (
                    <label className="flex items-center gap-2.5 cursor-pointer group glass-card rounded-xl px-3 py-1.5 border border-cyan-500/30">
                      <input
                        type="checkbox"
                        checked={isConstantMonitoring}
                        onChange={(e) => setIsConstantMonitoring(e.target.checked)}
                        className="w-4 h-4 glass-input border-2 border-glass-border-strong rounded cursor-pointer"
                      />
                      <span className="text-xs font-orbitron text-violet-200 group-hover:text-violet-100 transition-colors tracking-wider whitespace-nowrap">
                        CONSTANT MONITORING
                      </span>
                      <span 
                        className="text-violet-300/60 hover:text-violet-200 cursor-help transition-colors text-sm"
                        title="Enables instantaneous coverage for monitoring (vs nightly visits)"
                      >
                        ⓘ
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Projection Toggle */}
              <div className="mb-6 text-center">
                <div className="inline-flex border border-cyan-500/30 rounded-lg bg-black/50 backdrop-blur-xl p-1">
                  <button
                    onClick={() => setMapProjection('leaflet')}
                    className={`px-4 py-2 text-sm font-orbitron tracking-wider rounded transition-all ${
                      mapProjection === 'leaflet' 
                        ? 'glass-button-active text-sky-100' 
                        : 'text-sky-300/60 hover:text-sky-200 glass-button'
                    }`}
                  >
                    MAP VIEW
                  </button>
                  <button
                    onClick={() => setMapProjection('mollweide')}
                    className={`px-4 py-2 text-sm font-orbitron tracking-wider rounded transition-all ${
                      mapProjection === 'mollweide' 
                        ? 'glass-button-active text-sky-100' 
                        : 'text-sky-300/60 hover:text-sky-200 glass-button'
                    }`}
                  >
                    MOLLWEIDE VIEW
                  </button>
                </div>
              </div>

              <div className="flex gap-6">
                {/* Map Container - Conditional rendering for Leaflet vs Mollweide */}
                <div ref={containerRef} className="relative w-full h-[500px] bg-black/80 border border-cyan-500/30 rounded-lg overflow-hidden">
                  {mapProjection === 'leaflet' ? (
                    <MapContainer
                      center={[20, 0]}
                      zoom={2}
                      minZoom={2}
                      maxZoom={6}
                      zoomControl={false}
                      attributionControl={false}
                      worldCopyJump={false}
                      className="w-full h-full leaflet-dark-theme"
                      style={{ background: 'transparent' }}
                    >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    />
                    
                    {/* GEO Coverage Bands - Non-overlapping horizontal bands */}
                    {coverageType === 'geo' && (
                      <>
                        {geoCoverage.map((band, index) => {
                          const latRange = band.latRange;
                          const coverageOpacity = Math.min(band.coveragePercentage / 100, 1.0) * 0.6; // Cap at 100% and scale to max 0.6 opacity
                          
                          // Get the previous band's range (inner boundary for this band)
                          const prevLatRange = index > 0 ? geoCoverage[index - 1].latRange : 0;
                          
                          return (
                            <div key={`coverage-band-${latRange}`}>
                              {/* North band segment */}
                              <Rectangle
                                bounds={[[prevLatRange, -3600], [latRange, 3600]]}
                                pathOptions={{
                                  fillColor: coverageType === 'geo' ? '#c084fc' : '#22c55e',
                                  fillOpacity: coverageOpacity,
                                  color: 'transparent',
                                  weight: 0
                                }}
                              />
                              {/* South band segment */}
                              <Rectangle
                                bounds={[[-latRange, -3600], [-prevLatRange, 3600]]}
                                pathOptions={{
                                  fillColor: coverageType === 'geo' ? '#c084fc' : '#22c55e',
                                  fillOpacity: coverageOpacity,
                                  color: 'transparent',
                                  weight: 0
                                }}
                              />
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Telescope Network Markers - Grouped by Location with World Copies */}
                    {groupModulesByLocation(currentNetwork).map((groupedLocation, index) => {
                      // Create markers for multiple world copies (-360°, 0°, +360°)
                      const worldCopies = [-360, 0, 360];
                      
                      return worldCopies.map(offset => (
                        <Marker
                          key={`${groupedLocation.lat}-${groupedLocation.lon}-${offset}`}
                          position={[groupedLocation.lat, groupedLocation.lon + offset]}
                          icon={createCustomIcon(coverageType, groupedLocation.modules.length, groupedLocation.status)}
                        >
                          <Tooltip className="custom-tooltip" permanent={false} direction="top">
                            <div className="glass-modal rounded-lg px-3 py-2 text-sm font-mono text-sky-100">
                              <div className="font-semibold text-sky-100 text-base">{groupedLocation.location}</div>
                              {groupedLocation.modules.length > 1 ? (
                                <div className="text-sm opacity-70">{groupedLocation.modules.length} modules • {groupedLocation.totalTelescopes} telescopes</div>
                              ) : (
                                <div className="text-sm opacity-70">{groupedLocation.totalTelescopes} telescopes</div>
                              )}
                              <div className="text-xs opacity-50">Lat: {groupedLocation.lat}°, Lon: {groupedLocation.lon + offset}°</div>
                            </div>
                          </Tooltip>
                        </Marker>
                      ));
                    }).flat()}
                  </MapContainer>
                  ) : (
                    /* Mollweide Projection View */
                    <>
                      <svg 
                        ref={svgRef} 
                        className="w-full h-full cursor-grab active:cursor-grabbing"
                        style={{ background: 'transparent' }}
                      />
                      
                      {/* Zoom controls for Mollweide */}
                      <div className="absolute bottom-4 right-4 flex flex-col gap-1 z-[1000]">
                        <button
                          onClick={handleMollweideZoomIn}
                          className="w-8 h-8 bg-cyan-600/80 hover:bg-cyan-600/90 rounded flex items-center justify-center text-white transition-colors border border-cyan-500/50"
                          title="Zoom in"
                        >
                          <span className="text-lg font-bold">+</span>
                        </button>
                        <button
                          onClick={handleMollweideZoomOut}
                          className="w-8 h-8 bg-cyan-600/80 hover:bg-cyan-600/90 rounded flex items-center justify-center text-white transition-colors border border-cyan-500/50"
                          title="Zoom out"
                        >
                          <span className="text-lg font-bold">−</span>
                        </button>
                        <button
                          onClick={handleMollweideZoomReset}
                          className="w-8 h-8 bg-cyan-600/80 hover:bg-cyan-600/90 rounded flex items-center justify-center text-white transition-colors border border-cyan-500/50 text-xs"
                          title="Reset zoom"
                        >
                          ⟲
                        </button>
                      </div>
                    </>
                  )}

                  {/* Coverage Panel - GEO */}
                  {coverageType === 'geo' && mapProjection === 'leaflet' && (
                    <div className="absolute top-4 right-4 w-64 glass-card rounded-2xl p-3 z-[1000]">
                      <h3 className={`text-xs font-mono tracking-wider mb-3 ${isConstantMonitoring ? 'text-emerald-100' : 'text-violet-100'}`}>
                        GEO {isConstantMonitoring ? 'MONITORING' : 'SURVEY'}
                      </h3>
                      <div className="space-y-2">
                        {geoCoverage.map((band, index) => (
                          <div key={index} className={`bg-black/40 border rounded p-2 ${isConstantMonitoring ? 'border-green-500/20' : 'border-purple-500/20'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-3 h-3 rounded opacity-${band.color.includes('80') ? '80' : band.color.includes('60') ? '60' : band.color.includes('40') ? '40' : '20'} ${isConstantMonitoring ? 'bg-green-400' : 'bg-purple-400'}`}></div>
                              <span className={`text-xs font-mono ${isConstantMonitoring ? 'text-green-200' : 'text-purple-200'}`}>{band.name}</span>
                            </div>
                            <div className="text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="opacity-70">Coverage:</span>
                                <span className={`${band.coveragePercentage >= 100 ? (isConstantMonitoring ? 'text-cyan-400' : 'text-green-400') : band.coveragePercentage >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                  {Math.min(band.coveragePercentage, 100).toFixed(0)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="opacity-70">Redundancy:</span>
                                <span className={`${band.achievableCoverage >= band.requiredCoverageSqDeg ? (isConstantMonitoring ? 'text-cyan-400' : 'text-green-400') : 'text-gray-500'}`}>
                                  {band.achievableCoverage >= band.requiredCoverageSqDeg ? 
                                    `${(band.achievableCoverage / band.requiredCoverageSqDeg).toFixed(1)}x` : 
                                    '0.0x'
                                  }
                                </span>
                              </div>
                              {band.redundancy >= 1 && (
                                <div className="flex justify-between">
                                  <span className="opacity-70">Network Uptime:</span>
                                  <span className={`${calculateCoverageUptime(band.redundancy) >= 0.95 ? (isConstantMonitoring ? 'text-cyan-400' : 'text-green-400') : calculateCoverageUptime(band.redundancy) >= 0.90 ? 'text-yellow-400' : 'text-orange-400'}`}>
                                    {(calculateCoverageUptime(band.redundancy) * 100).toFixed(1)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Global Coverage Panel - Updated with redundancy calculation */}
                  {coverageType === 'global' && mapProjection === 'leaflet' && (
                    <div className="absolute top-4 right-4 w-56 glass-card rounded-2xl p-3 z-[1000]">
                      <h3 className="text-xs font-mono text-sky-100 tracking-wider mb-3">GLOBAL SURVEY</h3>
                      <div className="space-y-2">
                        <div className="bg-black/40 border border-cyan-500/20 rounded p-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-3 h-3 bg-cyan-400 opacity-60 rounded"></div>
                            <span className="text-xs font-mono text-cyan-200">Sky Coverage</span>
                          </div>
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="opacity-70">Coverage:</span>
                              <span className={`${coverage.totalSqDeg >= 41253 ? 'text-green-400' : coverage.totalSqDeg >= 20626.5 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {Math.min(((coverage.totalSqDeg / 41253) * 100), 100).toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="opacity-70">Redundancy:</span>
                              <span className={`${coverage.totalSqDeg >= 41253 ? 'text-green-400' : 'text-gray-500'}`}>
                                {coverage.totalSqDeg >= 41253 ? 
                                  `${(coverage.totalSqDeg / 41253).toFixed(1)}x` : 
                                  '0.0x'
                                }
                              </span>
                            </div>
                            {coverage.totalSqDeg >= 41253 && (
                              <div className="flex justify-between">
                                <span className="opacity-70">Network Uptime:</span>
                                <span className={`${calculateCoverageUptime(coverage.totalSqDeg / 41253) >= 0.95 ? 'text-green-400' : calculateCoverageUptime(coverage.totalSqDeg / 41253) >= 0.90 ? 'text-yellow-400' : 'text-orange-400'}`}>
                                  {(calculateCoverageUptime(coverage.totalSqDeg / 41253) * 100).toFixed(1)}%
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                                     {/* Current Network Panel */}
                   {coverageType === 'current' && mapProjection === 'leaflet' && (
                     <div className="absolute top-4 right-4 w-80 glass-card rounded-2xl p-3 z-[1000]">
                       <h3 className="text-xs font-mono text-sky-100 tracking-wider mb-3">CURRENT NETWORK</h3>
                       <div className="grid grid-cols-2 gap-2">
                         {currentDeployedLocations.map((location, index) => (
                           <div key={index} className="bg-black/40 border border-green-500/20 rounded p-2">
                             <div className="flex items-center gap-2 mb-1">
                               <div className="w-3 h-3 rounded bg-green-400"></div>
                               <span className="text-xs font-orbitron text-sky-100">{location.name}</span>
                             </div>
                             <div className="text-xs space-y-1">
                               <div className="flex justify-between">
                                 <span className="opacity-70">Location:</span>
                                 <span className="text-sky-100">{location.fullName}</span>
                               </div>
                               <div className="flex justify-between">
                                 <span className="opacity-70">Lat:</span>
                                 <span className="text-sky-100">{location.lat.toFixed(2)}°</span>
                               </div>
                               <div className="flex justify-between">
                                 <span className="opacity-70">Lon:</span>
                                 <span className="text-sky-100">{location.lon.toFixed(2)}°</span>
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coverage Outputs - Separate Section */}
        <div className="relative">
          <div className="relative border border-cyan-500/30 bg-black/50 backdrop-blur-xl p-4 lg:p-6 rounded-lg">
            <h2 className="text-lg lg:text-xl font-thin mb-6 lg:mb-8 tracking-wider flex items-center">
              COVERAGE OUTPUTS
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="p-4 bg-blue-900/10 border border-blue-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">TELESCOPE FOV</div>
                <div className="text-lg font-mono">
                  {(coverageType === 'geo' && isConstantMonitoring) ? '7.4' : fovData.areaSqDeg.toFixed(1)} sq°
                </div>
              </div>

              <div className="p-4 bg-green-900/10 border border-green-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">TOTAL SKY COVERAGE</div>
                <div className="text-lg font-mono">{coverage.totalSqDeg.toFixed(0)} sq°</div>
              </div>
              
              <div className="p-4 bg-green-900/10 border border-green-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">HEMISPHERE COVERAGE</div>
                <div className="text-lg font-mono">{coverage.hemispheres.toFixed(2)} hem</div>
              </div>
              
              <div className="p-4 bg-blue-900/10 border border-blue-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">POINTINGS/TELESCOPE</div>
                <div className="text-lg font-mono">{coverage.pointingsPerTelescope}</div>
              </div>
              
              <div className="p-4 bg-blue-900/10 border border-blue-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">TOTAL POINTINGS</div>
                <div className="text-lg font-mono">{coverage.totalPointings}</div>
              </div>
              
              <div className="p-4 bg-purple-900/10 border border-purple-500/20 rounded">
                <div className="text-xs opacity-60 mb-2">TOTAL TELESCOPES</div>
                <div className="text-lg font-mono">{networkParams.numSites[coverageType] * networkParams.modulesPerSite * telescopesPerModule}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Title moved to bottom */}
        <div className="mt-8 md:mt-12 text-center">
          <h1 className="text-3xl md:text-5xl font-thin tracking-widest mb-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
            SUTTER NETWORK VISUALIZER
          </h1>
          <div className="text-xs md:text-sm tracking-wider opacity-60">GROUND-BASED OBSERVATORY PLANNING SYSTEM v1.1</div>
        </div>
      </div>

      {/* Observing Parameters Popup */}
      {showObservingPopup && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setShowObservingPopup(false)}
        >
          <div 
            className="relative glass-modal rounded-3xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowObservingPopup(false)}
              className="absolute top-4 right-4 text-sky-200/80 hover:text-sky-100 text-xl transition-colors"
            >
              ✕
            </button>
            
            <h2 className="text-lg lg:text-xl font-thin mb-4 lg:mb-6 tracking-wider flex items-center text-sky-100">
              OBSERVING PARAMETERS
            </h2>
            
            <div className="space-y-6 lg:space-y-8">
              <div className="group">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-xs tracking-wider text-sky-200/60 group-hover:text-sky-100 transition-opacity">
                    EXPOSURE TIME (SEC)
                  </label>
                  <span className="text-sm font-orbitron text-sky-100">{networkParams.exposureTime}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={networkParams.exposureTime}
                  onChange={(e) => handleInputChange('exposureTime', e.target.value)}
                  className="glass-slider w-full"
                />
              </div>

              <div className="group">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-xs tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                    NUMBER OF EXPOSURES
                  </label>
                  <span className="text-sm font-orbitron text-sky-100">{networkParams.numExposures}</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="200"
                  step="5"
                  value={networkParams.numExposures}
                  onChange={(e) => handleInputChange('numExposures', e.target.value)}
                  className="glass-slider w-full"
                />
                <div className="text-xs text-sky-200/40 mt-2">{(networkParams.exposureTime * networkParams.numExposures / 60).toFixed(1)} min per stack</div>
              </div>

              <div className="group">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-xs tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                    NIGHT LENGTH (HOURS)
                  </label>
                  <span className="text-sm font-orbitron text-sky-100">{networkParams.nightLength}</span>
                </div>
                <input
                  type="range"
                  min="4"
                  max="16"
                  step="0.5"
                  value={networkParams.nightLength}
                  onChange={(e) => handleInputChange('nightLength', e.target.value)}
                  className="glass-slider w-full"
                />
              </div>

              <div className="group">
                <div className="flex justify-between items-center mb-3">
                  <label className={`text-xs tracking-wider transition-opacity ${
                    (coverageType === 'geo' && isConstantMonitoring) ? 'opacity-30' : 'opacity-60 group-hover:opacity-100'
                  }`}>
                    REVISITS PER NIGHT
                  </label>
                  <span className={`text-sm font-orbitron ${
                    (coverageType === 'geo' && isConstantMonitoring) ? 'text-sky-300/40' : 'text-sky-100'
                  }`}>
                    {(coverageType === 'geo' && isConstantMonitoring) ? 'N/A' : networkParams.revisitsPerNight}
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={networkParams.revisitsPerNight}
                  onChange={(e) => handleInputChange('revisitsPerNight', e.target.value)}
                  disabled={coverageType === 'geo' && isConstantMonitoring}
                  className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                    (coverageType === 'geo' && isConstantMonitoring) 
                      ? 'bg-gray-700/30 cursor-not-allowed' 
                      : 'bg-blue-900/30'
                  }`}
                />
                <div className={`text-xs mt-2 ${
                  (coverageType === 'geo' && isConstantMonitoring) ? 'text-gray-500' : 'opacity-40'
                }`}>
                  {(coverageType === 'geo' && isConstantMonitoring) 
                    ? 'Continuous monitoring - no revisits needed'
                    : `${(networkParams.nightLength * 60 / networkParams.revisitsPerNight).toFixed(0)} min between revisits`
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Projection Popup */}
      {showCostPopup && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setShowCostPopup(false)}
        >
          <div 
            className="relative glass-modal rounded-3xl p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowCostPopup(false)}
              className="absolute top-4 right-4 text-amber-200/80 hover:text-amber-100 text-xl transition-colors"
            >
              ✕
            </button>
            
            <h2 className="text-lg lg:text-xl font-thin mb-4 lg:mb-6 tracking-wider flex items-center text-amber-100 pr-10">
              COST PROJECTION
            </h2>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Cost Drivers Section */}
              <div>
                <h3 className="text-sm font-orbitron text-amber-100 tracking-wider mb-4 flex items-center">
                  COST DRIVERS
                </h3>
                
                <div className="glass-input rounded-2xl p-4">
                <div className="space-y-4">
                  {/* Number of Sites Slider */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-xs tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                        NUMBER OF SITES
                      </label>
                      <span className="text-sm font-orbitron text-amber-100">{networkParams.numSites[coverageType]}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max={(coverageType === 'geo' && isConstantMonitoring) ? "250" : "34"}
                      step="1"
                      value={networkParams.numSites[coverageType]}
                      onChange={(e) => handleInputChange('numSites', e.target.value)}
                      className="glass-slider w-full"
                    />
                  </div>

                  {/* Modules per Site Slider */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-xs tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                        MODULES PER SITE
                      </label>
                      <span className="text-sm font-orbitron text-amber-100">{networkParams.modulesPerSite}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="1"
                      value={networkParams.modulesPerSite}
                      onChange={(e) => handleInputChange('modulesPerSite', e.target.value)}
                      className="glass-slider w-full"
                    />
                  </div>

                  <CostProjectionHardwareSummary
                    hardwareConfig={hardwareConfig}
                    hardwareCostResult={hardwareCostResult}
                  />

                  {/* Contract Period Slider */}
                  <div className="group pt-2">
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-xs tracking-wider text-amber-200/60 group-hover:text-amber-100 transition-opacity">
                        CONTRACT PERIOD (YEARS)
                      </label>
                      <span className="text-sm font-orbitron text-amber-100">{costParams[coverageType].years}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="5"
                      step="0.5"
                      value={costParams[coverageType].years}
                      onChange={(e) => handleCostChange('years', e.target.value)}
                      className="glass-slider w-full"
                    />
                  </div>

                  {/* Service Options */}
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={costParams[coverageType].taOperated}
                        onChange={(e) => handleCostChange('taOperated', e.target.checked)}
                        className="w-5 h-5 glass-input border-2 border-glass-border-strong rounded cursor-pointer"
                      />
                      <span className="text-sm tracking-wider text-amber-200/80 group-hover:text-amber-100 transition-colors">
                        TRANSASTRA OPERATED
                      </span>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={costParams[coverageType].taMaintained}
                        onChange={(e) => handleCostChange('taMaintained', e.target.checked)}
                        className="w-5 h-5 glass-input border-2 border-glass-border-strong rounded cursor-pointer"
                      />
                      <span className="text-sm tracking-wider text-amber-200/80 group-hover:text-amber-100 transition-colors">
                        TRANSASTRA MAINTAINED
                      </span>
                    </label>
                  </div>
                </div>
                </div>
                
                {/* Total Price Card */}
                <div className="mt-4 bg-orange-600/20 border border-orange-500/50 rounded-2xl p-4">
                  {showPriceBreakdown ? (
                    <div className="space-y-2">
                      {costData.scaleMultiplier > 1 ? (
                        <div className="flex justify-between text-sm pb-2 border-b border-orange-500/20">
                          <span className="opacity-70">Linear subtotal</span>
                          <span className="font-orbitron text-amber-100/80 cost-value">
                            ${(costData.linearTotalCost / 1000000).toFixed(2)}M
                          </span>
                        </div>
                      ) : null}
                      {costData.scaleMultiplier > 1 ? (
                        <div className="flex justify-between text-sm">
                          <span className="opacity-70">Factory scale ({costData.scaleMultiplier.toFixed(2)}×)</span>
                          <span className="font-orbitron text-amber-100/80 cost-value">
                            ${((costData.totalCost - costData.linearTotalCost) / 1000000).toFixed(2)}M
                          </span>
                        </div>
                      ) : null}
                      <div className="flex justify-between text-sm">
                        <span className="opacity-70">Total Cost</span>
                        <span className="font-orbitron text-amber-100 cost-value">
                          ${(costData.totalCost / 1000000).toFixed(2)}M
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-70">Profit ({((costBaseParams.profitMargin - 1) * 100).toFixed(0)}%)</span>
                        <span className="font-orbitron text-amber-100 cost-value">
                          ${(costData.totalProfit / 1000000).toFixed(2)}M
                        </span>
                      </div>
                      <div className="flex justify-between text-lg border-t border-orange-500/30 pt-2">
                        <span className="font-orbitron tracking-wider font-bold">TOTAL PRICE</span>
                        <span className="font-orbitron font-bold text-orange-200 cost-value">
                          ${(costData.totalPrice / 1000000).toFixed(2)}M
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between text-lg">
                      <span className="font-orbitron tracking-wider font-bold">TOTAL PRICE</span>
                      <span className="font-orbitron font-bold text-orange-200 cost-value">
                        ${(costData.totalPrice / 1000000).toFixed(2)}M
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Costs Section */}
              <div>
                <div className="flex justify-between items-center mb-4 gap-2">
                  <h3 className="text-sm font-orbitron text-amber-100 tracking-wider flex items-center">
                    {costEditMode ? 'COSTS (EDITABLE)' : 'COSTS'}
                  </h3>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowHardwareCosting(true)}
                      className="glass-button px-3 py-1 text-xs font-orbitron tracking-wider rounded-lg text-amber-200"
                    >
                      HARDWARE COSTING
                    </button>
                    <button
                      type="button"
                      onClick={() => setCostEditMode(!costEditMode)}
                      className="glass-button px-3 py-1 text-xs font-orbitron tracking-wider rounded-lg text-amber-200"
                    >
                      {costEditMode ? 'VIEW SUMMARY' : 'EDIT BASE COSTS'}
                    </button>
                  </div>
                </div>
                
                {costEditMode ? (
                  /* Editable Table View - Auto-generated from Cost Items */
                  <div className="bg-orange-900/10 border border-orange-500/20 rounded overflow-hidden">
                    {/* Display Options */}
                    <div className="px-4 py-3 bg-orange-800/20 border-b border-orange-500/20 flex items-center justify-between">
                      <span className="text-xs font-orbitron text-amber-100 tracking-wider font-bold">DISPLAY OPTIONS</span>
                      <button
                        onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                        className="glass-button px-3 py-1 text-xs font-orbitron tracking-wider rounded-lg text-amber-200"
                      >
                        {showPriceBreakdown ? 'HIDE PROFIT' : 'SHOW PROFIT'}
                      </button>
                    </div>
                    
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 border-b border-orange-500/30 z-10">
                          <tr>
                            <th className="text-left px-4 py-3 font-orbitron text-orange-200 tracking-wider bg-orange-900 backdrop-blur-sm">PARAMETER</th>
                            <th className="text-right px-4 py-3 font-orbitron text-orange-200 tracking-wider bg-orange-900 backdrop-blur-sm">VALUE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Costs Section */}
                          <tr className="bg-orange-800/20">
                            <td colSpan="2" className="px-4 py-2 text-xs font-orbitron text-amber-100 tracking-wider font-bold">
                              UNIT COST ASSUMPTIONS (EDITABLE)
                            </td>
                          </tr>
                          
                          {/* Profit Margin */}
                          <tr className="border-b border-orange-500/10 hover:bg-orange-900/20">
                            <td className="px-4 py-2 opacity-70">Profit Margin (%)</td>
                            <td className="px-4 py-2 text-right">
                              <input
                                type="number"
                                step="1"
                                value={((costBaseParams.profitMargin - 1) * 100).toFixed(0)}
                                onChange={(e) => handleProfitMarginChange(parseFloat(e.target.value))}
                                className="w-24 px-2 py-1 glass-input border border-glass-border-strong rounded text-amber-100 font-mono text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </td>
                          </tr>
                          
                          {/* Dynamic Cost Items */}
                          {costBaseParams.costItems && costBaseParams.costItems.map((item) => (
                            <tr key={item.id} className="border-b border-orange-500/10 hover:bg-orange-900/20" title={getCostTooltip(item)}>
                              <td className="px-4 py-2 opacity-70 cursor-help">
                                {item.name}
                                {item.multiplier && ` (${item.multiplier} ${item.multiplierLabel || 'x'})`}
                                {item.period && ` - ${item.period.replace('per_', '')}`}
                                <span className="ml-1 text-amber-400/60 text-xs">ⓘ</span>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <input
                                  type="number"
                                  step={item.value >= 1000 ? "1000" : item.value >= 100 ? "100" : "10"}
                                  value={item.value}
                                  onChange={(e) => handleCostBaseChange(item.id, e.target.value)}
                                  className="w-32 px-2 py-1 glass-input border border-glass-border-strong rounded text-amber-100 font-mono text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </td>
                            </tr>
                          ))}
                          
                          {/* Calculated Outputs Section */}
                          <tr className="bg-orange-800/20">
                            <td colSpan="2" className="px-4 py-2 text-xs font-orbitron text-amber-100 tracking-wider font-bold">
                              CALCULATED OUTPUTS (READ-ONLY)
                            </td>
                          </tr>
                          
                          {/* One-Time Items */}
                          {costData.items
                            .filter(item => item.type === 'one-time')
                            .map(item => (
                              <tr key={`calc-${item.id}`} className="border-b border-orange-500/10 cursor-help" title={getCostTooltip(item)}>
                                <td className="px-4 py-2 opacity-70">
                                  Total {item.name}
                                  <span className="ml-1 text-amber-400/40 text-xs">ⓘ</span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-orange-200 cost-value">
                                  ${((showPriceBreakdown ? item.cost : item.price) / 1000000).toFixed(2)}M
                                </td>
                              </tr>
                            ))}
                          
                          <tr className="border-b border-orange-500/10 bg-orange-900/10">
                            <td className="px-4 py-2 font-semibold">One-Time Subtotal</td>
                            <td className="px-4 py-2 text-right font-mono text-orange-200 font-bold cost-value">
                              ${((showPriceBreakdown ? costData.oneTimeCost : costData.oneTimeCost * costBaseParams.profitMargin) / 1000000).toFixed(2)}M
                            </td>
                          </tr>
                          
                          {/* Recurring Items */}
                          {costData.items
                            .filter(item => item.type === 'recurring')
                            .map(item => (
                              <tr key={`calc-${item.id}`} className="border-b border-orange-500/10 cursor-help" title={getCostTooltip(item)}>
                                <td className="px-4 py-2 opacity-70">
                                  <span className={item.skipped ? 'line-through opacity-50' : ''}>
                                    Total {item.name}
                                    {!item.skipped && <span className="ml-1 text-amber-400/40 text-xs">ⓘ</span>}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-orange-200 cost-value">
                                  <span className={item.skipped ? 'text-gray-500' : ''}>
                                    ${((showPriceBreakdown ? item.cost : item.price) / 1000000).toFixed(2)}M
                                  </span>
                                </td>
                              </tr>
                            ))}
                          
                          <tr className="border-b border-orange-500/10 bg-orange-900/10">
                            <td className="px-4 py-2 font-semibold">Recurring Subtotal</td>
                            <td className="px-4 py-2 text-right font-mono text-orange-200 font-bold cost-value">
                              ${((showPriceBreakdown ? costData.recurringCost : costData.recurringCost * costBaseParams.profitMargin) / 1000000).toFixed(2)}M
                            </td>
                          </tr>
                          
                          {showPriceBreakdown ? (
                            <>
                              <tr className="border-b border-orange-500/10 bg-orange-900/10">
                                <td className="px-4 py-2 font-semibold">Total Cost</td>
                                <td className="px-4 py-2 text-right font-mono text-orange-200 font-bold cost-value">
                                  ${(costData.totalCost / 1000000).toFixed(2)}M
                                </td>
                              </tr>
                              <tr className="border-b border-orange-500/10">
                                <td className="px-4 py-2 opacity-70">Profit ({((costBaseParams.profitMargin - 1) * 100).toFixed(0)}%)</td>
                                <td className="px-4 py-2 text-right font-mono text-orange-200 cost-value">
                                  ${(costData.totalProfit / 1000000).toFixed(2)}M
                                </td>
                              </tr>
                              <tr className="bg-orange-600/30 border-t-2 border-orange-500/50">
                                <td className="px-4 py-3 font-orbitron text-orange-200 font-bold tracking-wider">TOTAL PRICE</td>
                                <td className="px-4 py-3 text-right font-orbitron text-orange-100 font-bold text-base cost-value">
                                  ${(costData.totalPrice / 1000000).toFixed(2)}M
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr className="bg-orange-600/30 border-t-2 border-orange-500/50">
                              <td className="px-4 py-3 font-orbitron text-orange-200 font-bold tracking-wider">TOTAL PRICE</td>
                              <td className="px-4 py-3 text-right font-orbitron text-orange-100 font-bold text-base cost-value">
                                ${(costData.totalPrice / 1000000).toFixed(2)}M
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  /* Default Display View - Auto-generated from Cost Items */
                  <div className="space-y-4" key={`cost-summary-${totalModules}-${costParams[coverageType].years}-${costParams[coverageType].taOperated}-${costParams[coverageType].taMaintained}`}>
                    {/* One-Time Costs */}
                    <div className="bg-orange-900/10 border border-orange-500/20 rounded p-4">
                      <div className="text-xs opacity-60 mb-3">ONE-TIME COSTS</div>
                      <div className="space-y-2">
                        {costData.items
                          .filter(item => item.type === 'one-time')
                          .map(item => (
                            <div key={item.id} className="flex justify-between text-sm cursor-help" title={getCostTooltip(item)}>
                              <span className="opacity-70">
                                {item.name}
                                <span className="ml-1 text-amber-400/40 text-xs">ⓘ</span>
                              </span>
                              <span className="font-orbitron text-amber-200/90 cost-value">
                                ${((showPriceBreakdown ? item.cost : item.price) / 1000000).toFixed(2)}M
                              </span>
                            </div>
                          ))}
                        <div className="flex justify-between text-sm font-bold border-t border-orange-500/20 pt-2">
                          <span>Subtotal</span>
                          <span className="font-mono text-orange-200 cost-value">
                            ${((showPriceBreakdown ? costData.oneTimeCost : costData.oneTimeCost * costBaseParams.profitMargin) / 1000000).toFixed(2)}M
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Recurring Costs */}
                    <div className="bg-orange-900/10 border border-orange-500/20 rounded p-4">
                      <div className="text-xs opacity-60 mb-3">
                        RECURRING COSTS ({costParams[coverageType].years} years)
                      </div>
                      <div className="space-y-2">
                        {costData.items
                          .filter(item => item.type === 'recurring')
                          .map(item => (
                            <div key={item.id} className="flex justify-between text-sm cursor-help" title={getCostTooltip(item)}>
                              <span className={`${item.skipped ? 'opacity-40 line-through' : 'opacity-70'}`}>
                                {item.name}
                                {!item.skipped && <span className="ml-1 text-amber-400/40 text-xs">ⓘ</span>}
                              </span>
                              <span className={`font-orbitron cost-value ${item.skipped ? 'text-sky-300/30' : 'text-amber-200/90'}`}>
                                ${((showPriceBreakdown ? item.cost : item.price) / 1000000).toFixed(2)}M
                              </span>
                            </div>
                          ))}
                        <div className="flex justify-between text-sm font-bold border-t border-orange-500/20 pt-2">
                          <span>Subtotal</span>
                          <span className="font-mono text-orange-200 cost-value">
                            ${((showPriceBreakdown ? costData.recurringCost : costData.recurringCost * costBaseParams.profitMargin) / 1000000).toFixed(2)}M
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <HardwareCostingModal
        open={showHardwareCosting}
        onClose={() => setShowHardwareCosting(false)}
        initialConfig={hardwareConfig}
        totalModules={totalModules}
        onApply={handleApplyHardwareCosting}
      />
    </div>
  );
};
function AuthLoading() {
  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
      <div className="glass-card rounded-2xl px-8 py-6 text-sky-200 font-orbitron tracking-wider animate-pulse">
        Loading...
      </div>
    </div>
  );
}

function App() {
  const { user, loading, signOut, configError } = useAuth();

  if (configError) {
    return <ConfigError />;
  }

  if (loading) {
    return <AuthLoading />;
  }

  if (!user) {
    return <LoginPage />;
  }

  return <TelescopeNetworkPlanner user={user} signOut={signOut} />;
}

export default App;